import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../../../context/usePortal';
import { transactionData } from '../../../../core/api/http-methods/get-user-transactions';
import TransactionListItem from './TransactionListItem';

const rowsPerPage = 10;

const RecentActivities: React.FC = () => {
  const { transactions: ayeTransactions, user } = useApp();
  const phoneNumber = user?.country_code + '-' + user?.user_phone_number;
  const [transactions, setTransactions] = useState<
    transactionData[] | undefined
  >(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [backendTotalPages, setBackendTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);

  const totalPages =
    backendTotalPages || Math.ceil((transactions?.length || 0) / rowsPerPage);
  const displayedTransactions = transactions || [];

  const fetchAndSortTransactions = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await ayeTransactions(
        phoneNumber,
        currentPage,
        rowsPerPage
      );
      if (response?.success && response?.data) {
        setTransactions(response.data.data);
        setTotalCount(response.data.totalCount);
        setBackendTotalPages(response.data.totalPages);
      } else {
        console.warn('Fetched transactions are undefined or not an array');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTableLoading(false);
    }
  }, [ayeTransactions, phoneNumber, currentPage]);

  useEffect(() => {
    fetchAndSortTransactions();
  }, [fetchAndSortTransactions]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h2 className="mx-auto ml-0 mt-8 max-w-6xl pb-6 text-lg font-medium leading-6 text-gray-900">
        Recent activity
      </h2>

      {/* Activity list (smallest breakpoint only) */}
      <div className="sm:margin-x-4 shadow sm:overflow-hidden">
        {tableLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <ul className="mt-2 divide-y divide-gray-200 overflow-hidden shadow">
              {displayedTransactions.map((transaction) => (
                <TransactionListItem
                  key={transaction.id}
                  transaction={transaction}
                />
              ))}
            </ul>

            <nav
              className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6"
              aria-label="Pagination"
            >
              <div className="block pe-3">
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * rowsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * rowsPerPage, totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div className="flex flex-1 justify-end">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))
                  }
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      prev < totalPages ? prev + 1 : prev
                    )
                  }
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </nav>
          </>
        )}
      </div>
    </div>
  );
};

export default RecentActivities;
